/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, parseISO, subDays } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

export default function OcorrenciasChamadosTab({ token }) {
  const [loading, setLoading] = useState(false);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [error, setError] = useState('');
  const [dataInicio, setDataInicio] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dataFim, setDataFim] = useState(format(new Date(), 'yyyy-MM-dd'));

  const API = '/api';

  const fetchOcorrencias = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('data_inicio', `${dataInicio}T00:00:00`);
      if (dataFim) params.append('data_fim', `${dataFim}T23:59:59`);
      params.append('limit', '100');

      const res = await fetch(`${API}/chamados/auditoria/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOcorrencias(data);
      } else if (res.status === 403) {
        setError('Acesso restrito a administradores');
      } else {
        setError('Erro ao carregar ocorrências');
      }
    } catch (e) {
      setError('Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, [token, API, dataInicio, dataFim]);

  useEffect(() => {
    fetchOcorrencias();
  }, [fetchOcorrencias]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(parseISO(dateStr), "dd/MM/yy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getAcaoIcon = (acao) => {
    switch (acao?.toUpperCase()) {
      case 'CREATE': return <AddIcon fontSize="small" />;
      case 'UPDATE': return <EditIcon fontSize="small" />;
      case 'DELETE': return <DeleteIcon fontSize="small" />;
      default: return <EditIcon fontSize="small" />;
    }
  };

  const getAcaoColor = (acao) => {
    switch (acao?.toUpperCase()) {
      case 'CREATE': return 'success';
      case 'UPDATE': return 'warning';
      case 'DELETE': return 'error';
      default: return 'info';
    }
  };

  const formatCampo = (campo) => {
    const nomes = {
      'titulo': 'Título',
      'descricao': 'Descrição',
      'status': 'Status',
      'prioridade': 'Prioridade',
      'tipo': 'Tipo',
      'cliente_id': 'Cliente',
      'atendente': 'Atendente',
      'pendencia': 'Pendência',
      'foto': 'Foto',
      'audio': 'Áudio',
      'video': 'Vídeo',
    };
    return nomes[campo] || campo;
  };

  const formatValor = (valor) => {
    if (!valor) return '-';
    try {
      const parsed = JSON.parse(valor);
      if (Array.isArray(parsed)) {
        return parsed.length > 0 ? `${parsed.length} item(s)` : '-';
      }
      return String(parsed);
    } catch {
      return valor.length > 50 ? valor.substring(0, 50) + '...' : valor;
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        📋 Ocorrências de Edição em Chamados
      </Typography>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Data Início"
          type="date"
          value={dataInicio}
          onChange={(e) => setDataInicio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Data Fim"
          type="date"
          value={dataFim}
          onChange={(e) => setDataFim(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button variant="contained" onClick={fetchOcorrencias} disabled={loading}>
          Buscar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data/Hora</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Chamado</TableCell>
                <TableCell>Ação</TableCell>
                <TableCell>Campo</TableCell>
                <TableCell>Valor Anterior</TableCell>
                <TableCell>Valor Novo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ocorrencias.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhuma ocorrência encontrada no período
                  </TableCell>
                </TableRow>
              ) : (
                ocorrencias.map((oc) => (
                  <TableRow key={oc.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                      {formatDateTime(oc.data_hora)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>
                      {oc.usuario_nome}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`#${oc.chamado_id}`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getAcaoIcon(oc.acao)}
                        label={oc.acao}
                        color={getAcaoColor(oc.acao)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {formatCampo(oc.campo_alterado)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {formatValor(oc.valor_anterior)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {formatValor(oc.valor_novo)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Exibindo {ocorrencias.length} ocorrências
      </Typography>
    </Box>
  );
}
